/** Signer-picker modal launched from a chat PDF evidence card.
 *
 * Operator fills in one or more signers (name + email + phone +
 * language + auth-type + automation flag) and submits. Submission
 * fires POST /v1/evidence/<id>/sign which returns the SignatureTransaction
 * row; the parent surface keeps the transaction id and starts polling
 * via SignatureStatusCard.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import PhoneInput from "@/components/PhoneInput";
import { api } from "@/lib/api";
import type { SignatureTransaction } from "@/lib/types";

import {
  fetchSignableParticipants,
  kickoffSignature,
  type SignableParticipant,
  type SignerInput,
} from "./api";


type DefaultSigner = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  authentication_type?: "sms" | "email";
  language?: string;
};


type SignerDraft = SignerInput & {
  _key: string;
  /** Which required fields were still blank on the picked participant —
   *  drives the "complete these to sign" hint. UI-only. */
  _missing?: string[];
};


function emptySigner(order: number): SignerDraft {
  return {
    _key: `s-${order}-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
    order,
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    language: "fr",
    authentication_type: "sms",
    automation: false,
    contact_id: null,
    user_id: null,
  };
}


export function SignDocumentModal({
  evidenceId,
  sessionId,
  initialSigners,
  onClose,
  onLaunched,
}: {
  evidenceId: string;
  /** Used to fetch the session's default signer (beneficiary's
   *  email/phone/name) so the modal opens pre-filled. Optional —
   *  callers that already pass `initialSigners` can skip it. */
  sessionId?: string;
  initialSigners?: SignerInput[];
  onClose: () => void;
  onLaunched: (txn: SignatureTransaction) => void;
}) {
  const { t } = useTranslation();
  const [language, setLanguage] = useState("fr");
  const [signers, setSigners] = useState<SignerDraft[]>(() => {
    if (initialSigners && initialSigners.length > 0) {
      return initialSigners.map((s, i) => ({
        ...emptySigner(i + 1),
        ...s,
        order: i + 1,
      }));
    }
    return [emptySigner(1)];
  });

  // Phase signature-prefill — pull the session's default signer from
  // the backend (membership.contact > field invite > participant
  // display_name) so the operator doesn't retype the beneficiary's
  // details. Skipped when `initialSigners` is already supplied OR
  // no `sessionId` is available.
  const shouldPrefetch = !!sessionId && !initialSigners;
  const defaultSigner = useQuery({
    queryKey: ["session-default-signer", sessionId],
    queryFn: async () => {
      const res = await api.get<DefaultSigner>(
        `/v1/sessions/${sessionId}/default-signer`,
      );
      return res.data;
    },
    enabled: shouldPrefetch,
    retry: false,
  });

  // "Send to a participant" — the session's participants with their
  // best-known identity, so the operator picks the field user / beneficiary
  // instead of retyping (and we can flag what's missing).
  const participants = useQuery({
    queryKey: ["signable-participants", sessionId],
    queryFn: () => fetchSignableParticipants(sessionId as string),
    enabled: !!sessionId,
    retry: false,
  });

  // Multi-party targeting (backlog §9.4, 2026-09-08): `default-signer`
  // is a single best-guess inference (tiered, biased toward the NEWEST
  // field participant — see SessionDefaultSignerView) — reported bug:
  // "sending a document for signature auto-picks the wrong field
  // participant... once a 3rd joins, that one gets treated as the
  // signer." With more than one field-role entry in
  // `signable-participants`, silently trusting that single guess is
  // exactly the failure mode reported, so the auto-fill is skipped
  // entirely — the operator must use the "Send to" picker below
  // (already lists every participant explicitly) instead of a guess
  // they might not notice is wrong. Skip is deferred until
  // `participants.data` has loaded so the common single-field-
  // participant case (the vast majority of sessions) still gets its
  // familiar instant pre-fill rather than waiting on a second request.
  const fieldParticipantCount =
    participants.data?.filter((p) => p.role === "field").length ?? 0;
  const tooManyFieldParticipantsToGuess =
    !!participants.data && fieldParticipantCount > 1;

  useEffect(() => {
    if (!defaultSigner.data) return;
    if (tooManyFieldParticipantsToGuess) return;
    setSigners((current) => {
      // Only fill the FIRST signer slot, and only if the operator
      // hasn't already typed something in. Avoids stomping over edits
      // when the query resolves late.
      const head = current[0];
      const isHeadEmpty =
        !head?.first_name.trim() &&
        !head?.last_name.trim() &&
        !head?.email.trim() &&
        !head?.phone?.trim();
      if (!isHeadEmpty) return current;
      const filled: SignerDraft = {
        ...emptySigner(1),
        first_name: defaultSigner.data.first_name || "",
        last_name: defaultSigner.data.last_name || "",
        email: defaultSigner.data.email || "",
        phone: defaultSigner.data.phone || "",
        authentication_type:
          defaultSigner.data.authentication_type ?? "sms",
        language: defaultSigner.data.language ?? "fr",
        order: 1,
      };
      return [filled, ...current.slice(1)];
    });
    if (defaultSigner.data.language) {
      setLanguage(defaultSigner.data.language);
    }
  }, [defaultSigner.data, tooManyFieldParticipantsToGuess]);

  function applyParticipant(key: string, p: SignableParticipant) {
    updateSigner(key, {
      first_name: p.first_name || "",
      last_name: p.last_name || "",
      email: p.email || "",
      phone: p.phone || "",
      contact_id: p.contact_id,
      user_id: p.user_id,
      authentication_type: p.phone ? "sms" : "email",
      _missing: p.missing,
    });
  }

  const launch = useMutation({
    mutationFn: async () => {
      const payload = {
        language,
        signers: signers.map(({ _key, _missing, ...rest }) => ({
          ...rest,
          order: rest.order,
        })),
      };
      return kickoffSignature(evidenceId, payload);
    },
    onSuccess: (txn) => {
      onLaunched(txn);
      onClose();
    },
  });

  function updateSigner(key: string, patch: Partial<SignerDraft>) {
    setSigners((rows) =>
      rows.map((row) => (row._key === key ? { ...row, ...patch } : row)),
    );
  }

  function addSigner() {
    setSigners((rows) => [...rows, emptySigner(rows.length + 1)]);
  }

  function removeSigner(key: string) {
    setSigners((rows) =>
      rows
        .filter((row) => row._key !== key)
        .map((row, i) => ({ ...row, order: i + 1 })),
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (signers.length === 0) return;
    launch.mutate();
  }

  const valid =
    signers.length > 0 &&
    signers.every(
      (s) =>
        s.first_name.trim() &&
        s.last_name.trim() &&
        s.email.trim() &&
        (s.phone || "").trim(),
    );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("signature.modal.title", "Send for signature")}
      className="modal-backdrop bg-black/70"
    >
      <form
        onSubmit={onSubmit}
        className="modal-panel w-full max-w-xl space-y-4 max-h-[90vh] overflow-y-auto"
      >
        <header>
          <h2 className="text-lg font-semibold">
            {t("signature.modal.title", "Send for signature")}
          </h2>
          <p className="text-xs text-nb-text-3">
            {t(
              "signature.modal.hint",
              "Each signer gets a one-time link via SMS or email and signs the PDF in their browser.",
            )}
          </p>
          {tooManyFieldParticipantsToGuess && (
            <p className="mt-1 text-xs text-amber-400" role="alert">
              {t(
                "signature.modal.multipleFieldParticipants",
                "This session has more than one field participant — pick who should sign below instead of trusting the pre-filled name.",
              )}
            </p>
          )}
        </header>

        <label className="block text-xs text-nb-text-2">
          <span>{t("signature.modal.language", "Default language")}</span>
          <select
            className="input mt-1"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="de">Deutsch</option>
            <option value="it">Italiano</option>
          </select>
        </label>

        <div className="space-y-3">
          {signers.map((s, idx) => (
            <fieldset
              key={s._key}
              className="rounded-md border border-nb-border p-3 space-y-2"
            >
              <legend className="flex items-center justify-between gap-2 px-1 text-xs text-nb-text-2">
                <span>
                  {t("signature.modal.signer", "Signer")} #{idx + 1}
                </span>
                {signers.length > 1 && (
                  <button
                    type="button"
                    className="text-cvp-danger hover:underline"
                    onClick={() => removeSigner(s._key)}
                  >
                    {t("common.remove", "Remove")}
                  </button>
                )}
              </legend>

              {/* Send to a participant vs someone else — pick a participant
                  to prefill their known identity; blanks are the missing
                  fields the operator must complete before signing. */}
              {(participants.data?.length ?? 0) > 0 && (
                <label className="block px-1 text-xs text-nb-text-2">
                  <span>
                    {t("signature.modal.sendTo", "Send to")}
                  </span>
                  <select
                    className="input mt-1 w-full"
                    value=""
                    onChange={(e) => {
                      const p = participants.data?.find(
                        (x) => x.participant_id === e.target.value,
                      );
                      if (p) applyParticipant(s._key, p);
                    }}
                  >
                    <option value="">
                      {t(
                        "signature.modal.pickParticipant",
                        "Choose a participant… (or enter someone else below)",
                      )}
                    </option>
                    {participants.data?.map((p) => (
                      <option key={p.participant_id} value={p.participant_id}>
                        {p.label}
                        {p.missing.length > 0
                          ? ` — ${t("signature.modal.missingInfo", "missing info")}`
                          : ""}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {s._missing && s._missing.length > 0 && (
                <p className="px-1 text-xs text-amber-400">
                  {t(
                    "signature.modal.completeMissing",
                    "This information is required to sign — please complete the highlighted fields.",
                  )}
                </p>
              )}

              <div className="grid grid-cols-2 gap-2 text-xs">
                <label>
                  <span className="block text-nb-text-2">
                    {t("contacts.col.firstName", "First name")} *
                  </span>
                  <input
                    className="input mt-1 w-full"
                    value={s.first_name}
                    onChange={(e) =>
                      updateSigner(s._key, { first_name: e.target.value })
                    }
                    required
                  />
                </label>
                <label>
                  <span className="block text-nb-text-2">
                    {t("contacts.col.lastName", "Last name")} *
                  </span>
                  <input
                    className="input mt-1 w-full"
                    value={s.last_name}
                    onChange={(e) =>
                      updateSigner(s._key, { last_name: e.target.value })
                    }
                    required
                  />
                </label>
                <label>
                  <span className="block text-nb-text-2">
                    {t("contacts.col.email", "Email")} *
                  </span>
                  <input
                    type="email"
                    className="input mt-1 w-full"
                    value={s.email}
                    onChange={(e) =>
                      updateSigner(s._key, { email: e.target.value })
                    }
                    required
                  />
                </label>
                <label>
                  <span className="block text-nb-text-2">
                    {t("contacts.col.phone", "Phone")} *
                  </span>
                  <PhoneInput
                    className="mt-1"
                    value={s.phone || ""}
                    onChange={(v) => updateSigner(s._key, { phone: v })}
                    required
                  />
                </label>
                <label>
                  <span className="block text-nb-text-2">
                    {t("signature.modal.signerLanguage", "Language")}
                  </span>
                  <select
                    className="input mt-1 w-full"
                    value={s.language || "fr"}
                    onChange={(e) =>
                      updateSigner(s._key, { language: e.target.value })
                    }
                  >
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="de">Deutsch</option>
                    <option value="it">Italiano</option>
                  </select>
                </label>
                <label>
                  <span className="block text-nb-text-2">
                    {t("signature.modal.auth", "Auth")}
                  </span>
                  <select
                    className="input mt-1 w-full"
                    value={s.authentication_type || "sms"}
                    onChange={(e) =>
                      updateSigner(s._key, {
                        authentication_type: e.target.value as
                          | "sms"
                          | "email",
                      })
                    }
                  >
                    <option value="sms">SMS</option>
                    <option value="email">Email</option>
                  </select>
                </label>
                {/* No per-signer "automation" toggle: automation=1 means
                    the signature is auto-embedded server-side with NO
                    email/SMS invitation, which is only ever valid for the
                    operator's own signature. Every party added here must
                    receive + sign the document, so they always go out as
                    automation=0 (the SignerDraft default). */}
              </div>
            </fieldset>
          ))}
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={addSigner}
          >
            + {t("signature.modal.addSigner", "Add another signer")}
          </button>
        </div>

        {launch.isError && (
          <p className="text-xs text-cvp-danger">{String(launch.error)}</p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onClose}
          >
            {t("common.cancel", "Cancel")}
          </button>
          <button
            type="submit"
            className="btn-primary text-sm"
            disabled={!valid || launch.isPending}
          >
            {launch.isPending
              ? t("signature.modal.sending", "Sending…")
              : t("signature.modal.send", "Send for signature")}
          </button>
        </div>
      </form>
    </div>
  );
}
